import { UkDateInput, ukDate } from "@/components/UkDateInput";

import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plane, Check, X, Trash2, Plus, Upload } from "lucide-react";
import { HeaderAccent } from "@/lib/status";
import { LeaveCalendar } from "@/components/LeaveCalendar";
import { CertButtons, uploadCert } from "@/components/SickCert";

const STATUS = { pending: "bg-gold-light text-gold-dark", approved: "bg-sage-light text-sage-dark", declined: "bg-coral-light text-coral-dark" };

export default function Leave() {
  const { user } = useAuth();
  const isManager = ["admin", "office"].includes(user?.role);
  const isCarer = user?.role === "carer";
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const EMPTY = { subject_type: "staff", subject_id: "", start_date: "", end_date: "", reason: "", kind: "leave" };
  const [f, setF] = useState(EMPTY);
  const [certFile, setCertFile] = useState(null);
  const load = () => api.get("/leave").then((r) => setRows(r.data));
  useEffect(() => {
    load();
    if (isManager) {
      api.get("/staff").then((r) => setStaff(r.data.filter((s) => ["carer", "admin", "office"].includes(s.role)))).catch(() => {});
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }
  }, [isManager]);
  const people = f.subject_type === "staff" ? staff : clients;
  const openNew = () => { setF(isCarer ? { ...EMPTY, subject_type: "staff", subject_id: user.id } : EMPTY); setOpen(true); };
  const create = async () => {
    const payload = isCarer ? { ...f, subject_type: "staff", subject_id: user.id } : f;
    if (!payload.subject_id || !payload.start_date || !payload.end_date) { toast.error(isCarer ? "Choose both dates" : "Choose a person and both dates"); return; }
    try {
      const { data } = await api.post("/leave", payload);
      if (payload.kind === "sickness" && certFile && data?.id) {
        try { await uploadCert(data.id, certFile); } catch (e) { toast.error(`Request saved, but the certificate didn't upload: ${apiError(e)}`); }
      }
      toast.success("Request submitted"); setOpen(false); setF(EMPTY); setCertFile(null); load();
    }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = async (id, status) => { try { await api.put(`/leave/${id}/status`, { status }); toast.success(`Request ${status}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id) => { if (!window.confirm("Delete this request? It can be restored from the Recycle bin for 7 days.")) return; try { await api.delete(`/leave/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const pending = rows.filter((r) => r.status === "pending");
  const others = rows.filter((r) => r.status !== "pending");
  const inp = "w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

  const Row = ({ r }) => (
    <div data-testid={`leave-${r.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-white p-4 shadow-sm">
      <div className="min-w-0 flex-1 basis-full sm:basis-0">
        <p className="font-semibold text-ink">{r.subject_name} <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium capitalize text-ink-soft">{r.subject_type === "client" ? "client holiday" : "staff leave"}</span>{r.kind === "sickness" && <span className="ml-2 rounded-full bg-coral-light px-2 py-0.5 text-[11px] font-semibold text-coral-dark">Sickness</span>}</p>
        <p className="text-sm text-ink-soft">{ukDate(r.start_date)} → {ukDate(r.end_date)}{r.reason ? ` · ${r.reason}` : ""}</p>
        <p className="text-xs text-ink-muted">Requested by {r.requested_by}{r.reviewed_by ? ` · reviewed by ${r.reviewed_by}` : ""}</p>
        {r.kind === "sickness" && <div className="mt-1.5 flex flex-wrap items-center gap-2"><CertButtons r={r} canUpload={isManager || r.subject_id === user?.id} onDone={load} /></div>}
      </div>
      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${STATUS[r.status]}`}>{r.status}</span>
      {isManager && r.status === "pending" && (
        <>
          <button onClick={() => setStatus(r.id, "approved")} data-testid={`approve-leave-${r.id}`} className="inline-flex items-center gap-1 rounded-lg bg-sage px-3 py-1.5 text-sm font-semibold text-white hover:brightness-105"><Check size={15} /> Approve</button>
          <button onClick={() => setStatus(r.id, "declined")} data-testid={`decline-leave-${r.id}`} className="inline-flex items-center gap-1 rounded-lg border border-coral px-3 py-1.5 text-sm font-semibold text-coral hover:bg-coral-light"><X size={15} /> Decline</button>
        </>
      )}
      {isManager && (
        <button onClick={() => del(r.id)} data-testid={`delete-leave-${r.id}`} title="Delete" className="text-ink-muted hover:text-coral"><Trash2 size={16} /></button>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <HeaderAccent />
          <h1 className="font-heading text-3xl font-bold text-ink">{isCarer ? "My leave & time off" : "Leave & Holidays"}</h1>
          <p className="text-ink-soft">{isCarer ? "Request holiday or time off in advance — the office will approve it." : "Time off for befrienders and holidays for the people we support."}</p>
        </div>
        <button onClick={openNew} data-testid="new-leave-button" className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-brand px-4 py-2.5 font-semibold text-white hover:bg-brand-dark"><Plus size={18} /> {isCarer ? "Request time off" : "New request"}</button>
      </div>

      {isManager && (
        <div className="flex gap-2" data-testid="leave-view-toggle">
          {[["list", "List"], ["calendar", "Month calendar"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`leave-view-${k}`}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${view === k ? "bg-ink text-white" : "bg-secondary text-ink hover:bg-brand-light"}`}>{l}</button>
          ))}
        </div>
      )}

      {isManager && view === "calendar" && <LeaveCalendar rows={rows} carerIds={new Set(staff.filter((s) => s.role === "carer" && s.status !== "inactive").map((s) => s.id))} />}

      {(!isManager || view === "list") && <>
      {isManager && pending.length > 0 && (
        <div>
          <h2 className="mb-2 flex items-center gap-2 font-heading text-lg font-semibold text-ink"><Plane size={18} className="text-gold-dark" /> Pending approval ({pending.length})</h2>
          <div className="space-y-2">{pending.map((r) => <Row key={r.id} r={r} />)}</div>
        </div>
      )}

      <div>
        <h2 className="mb-2 font-heading text-lg font-semibold text-ink">{isCarer ? "My requests" : "All requests"}</h2>
        {rows.length === 0 ? <p className="text-ink-muted">{isCarer ? "You haven't requested any time off yet." : "No leave or holiday requests yet."}</p> : <div className="space-y-2">{(isCarer ? rows : others).map((r) => <Row key={r.id} r={r} />)}</div>}
      </div>
      </>}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative z-10 max-h-[92vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-testid="leave-modal">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-heading text-xl font-bold text-ink">{isCarer ? "Request time off" : "New request"}</h2><button onClick={() => setOpen(false)}><X size={20} className="text-ink-muted" /></button></div>
            <div className="space-y-3">
              <div className="flex gap-2" data-testid="leave-kind-picker">
                {[["leave", "Holiday / leave"], ["sickness", "Sickness / absence"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setF({ ...f, kind: k })} className={`flex-1 rounded-lg py-2 text-sm font-semibold ${f.kind === k ? (k === "sickness" ? "bg-coral text-white" : "bg-brand text-white") : "border border-border text-ink-soft"}`}>{lbl}</button>
                ))}
              </div>
              {isManager && (
                <>
                  <div className="flex gap-2" data-testid="leave-type-picker">
                    {["staff", "client"].map((t) => (
                      <button key={t} onClick={() => setF({ ...f, subject_type: t, subject_id: "" })} className={`flex-1 rounded-lg py-2 text-sm font-semibold ${f.subject_type === t ? "bg-brand text-white" : "border border-border text-ink-soft"}`}>{t === "staff" ? "Befriender / staff" : "Client holiday"}</button>
                    ))}
                  </div>
                  <select className={inp} value={f.subject_id} onChange={(e) => setF({ ...f, subject_id: e.target.value })} data-testid="leave-person-select">
                    <option value="">Select person…</option>
                    {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </>
              )}
              <div className="flex gap-2">
                <div className="flex-1"><label className="mb-1 block text-xs font-medium text-ink-muted">From</label><UkDateInput value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} testid="leave-start" /></div>
                <div className="flex-1"><label className="mb-1 block text-xs font-medium text-ink-muted">To</label><UkDateInput value={f.end_date} min={f.start_date} onChange={(v) => setF({ ...f, end_date: v })} testid="leave-end" /></div>
              </div>
              <textarea rows={2} className={inp} placeholder="Reason (optional)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
              {f.kind === "sickness" && f.subject_type === "staff" && (
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-sm text-ink-soft hover:bg-secondary" data-testid="leave-cert-picker">
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setCertFile(e.target.files[0] || null)} data-testid="leave-cert-input" />
                  <Upload size={15} /> {certFile ? certFile.name : "Attach sick certificate or fit note (PDF or photo, optional — you can add it later)"}
                </label>
              )}
              <button onClick={create} data-testid="submit-leave-button" className="w-full rounded-lg bg-brand py-2.5 font-semibold text-white hover:bg-brand-dark">{isCarer ? "Submit request" : "Create request"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
